import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axiosInstance from "../../../../Config/axiosInstance";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./ProjectCodeDialog.css";

const animatedComponents = makeAnimated();

const PREVIEW_URL = "/code_registries/preview/";
const OPPORTUNITIES_URL = "/opportunities/";
const ORGANIZATIONS_URL = "/organizations/";

const initialErrors = {
  organization: "",
  department: "",
  activity: "",
  client: "",
  description: "",
  opportunities: "",
  code: "",
  submit: "",
};

export default function ProjectCodeCreateDialog({ open, onClose, onCreated }) {
  const [organizations, setOrganizations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [clients, setClients] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  const [organization, setOrganization] = useState(null);
  const [department, setDepartment] = useState(null);
  const [activity, setActivity] = useState(null);
  const [client, setClient] = useState(null);
  const [selectedOpportunities, setSelectedOpportunities] = useState([]);

  const [description, setDescription] = useState("");
  const [remarks, setRemarks] = useState("");
  const [, setNr] = useState("");
  const [code, setCode] = useState("");

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState(initialErrors);

  const aliveRef = useRef(false);
  const previewReqIdRef = useRef(0);

  const disabledAll = saving || generating || loadingLists;

  const extractErrorMessage = useCallback((e) => {
    const data = e?.response?.data;

    if (typeof data === "string") return data;
    if (data?.detail) return data.detail;

    if (data && typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      if (firstKey) {
        const value = data[firstKey];
        if (Array.isArray(value)) return value.join(", ");
        if (typeof value === "string") return value;
      }
    }

    return e?.message || "Eroare necunoscută";
  }, []);

  const setSubmitError = useCallback(
    (e) => {
      if (!aliveRef.current) return;
      setErrors((prev) => ({ ...prev, submit: extractErrorMessage(e) }));
    },
    [extractErrorMessage]
  );

  const clearSubmitError = useCallback(() => {
    setErrors((prev) => ({ ...prev, submit: "" }));
  }, []);

  const resetForm = useCallback(() => {
    setOrganization(null);
    setDepartment(null);
    setActivity(null);
    setClient(null);
    setSelectedOpportunities([]);
    setOpportunities([]);
    setDescription("");
    setRemarks("");
    setNr("");
    setCode("");
    setSaving(false);
    setGenerating(false);
    setSubmitted(false);
    setErrors(initialErrors);
    previewReqIdRef.current += 1;
  }, []);

  useEffect(() => {
    if (!open) {
      aliveRef.current = false;
      return;
    }

    aliveRef.current = true;
    resetForm();

    let cancelled = false;

    const loadLists = async () => {
      setLoadingLists(true);
      clearSubmitError();

      try {
        const [orgRes, depRes, actRes, cliRes] = await Promise.all([
          axiosInstance.get(ORGANIZATIONS_URL),
          axiosInstance.get("/departments/"),
          axiosInstance.get("/code_matrix/"),
          axiosInstance.get("/clients/"),
        ]);

        if (cancelled || !aliveRef.current) return;

        setOrganizations(orgRes.data || []);
        setDepartments(depRes.data || []);
        setActivities(actRes.data || []);
        setClients(cliRes.data || []);
        setOpportunities([]);
        setSelectedOpportunities([]);
      } catch (e) {
        if (!cancelled) setSubmitError(e);
      } finally {
        if (!cancelled && aliveRef.current) setLoadingLists(false);
      }
    };

    loadLists();

    return () => {
      cancelled = true;
      aliveRef.current = false;
    };
  }, [open, resetForm, setSubmitError, clearSubmitError]);

  useEffect(() => {
    if (!open) return;

    if (!client?.value) {
      setOpportunities([]);
      setSelectedOpportunities([]);
      return;
    }

    let cancelled = false;

    const loadOpps = async () => {
      try {
        const res = await axiosInstance.get(
          `${OPPORTUNITIES_URL}?unassigned=1&client_id=${client.value}`
        );

        if (cancelled || !aliveRef.current) return;

        const list = res.data || [];
        setOpportunities(list);

        const validIds = new Set(list.map((o) => o.id));
        setSelectedOpportunities((prev) =>
          (prev || []).filter((x) => validIds.has(x.value))
        );
      } catch (e) {
        if (!cancelled) setSubmitError(e);
      }
    };

    loadOpps();

    return () => {
      cancelled = true;
    };
  }, [open, client?.value, setSubmitError]);

  const organizationOptions = useMemo(
    () =>
      organizations.map((o) => ({
        value: o.id,
        label: `${o.name}${o.abbreviation ? ` (${o.abbreviation})` : ""}`,
      })),
    [organizations]
  );

  const departmentOptions = useMemo(
    () =>
      departments.map((d) => ({
        value: d.id,
        label: `${d.name}${
          d.department_abbreviated ? ` (${d.department_abbreviated})` : ""
        }`,
      })),
    [departments]
  );

  const activityOptions = useMemo(() => {
    const depId = department?.value;

    const filtered = depId
      ? activities.filter((m) => m.department === depId)
      : activities;

    return filtered.map((m) => ({
      value: m.id,
      label: `${m.activity}${
        m.activity_abbreviated ? ` (${m.activity_abbreviated})` : ""
      }`,
    }));
  }, [activities, department?.value]);

  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [clients]
  );

  const opportunityOptions = useMemo(
    () =>
      opportunities.map((o) => {
        const oppCode = o.code_opportunity?.trim() || `OPP#${o.id}`;
        const title =
          o.title_opportunity?.trim() || o.project_name?.trim() || "";

        return {
          value: o.id,
          label: title ? `${oppCode} - ${title}` : oppCode,
        };
      }),
    [opportunities]
  );

  const validateAll = useCallback(() => {
    const next = { ...initialErrors };

    if (!organization) next.organization = "Required";
    if (!department) next.department = "Required";
    if (!activity) next.activity = "Required";
    if (!client) next.client = "Required";
    if (!description.trim()) next.description = "Required";
    if (!code.trim()) next.code = "Required";

    setErrors((prev) => ({ ...prev, ...next }));

    return (
      !next.organization &&
      !next.department &&
      !next.activity &&
      !next.client &&
      !next.description &&
      !next.code
    );
  }, [organization, department, activity, client, description, code]);

  useEffect(() => {
    if (!open) return;

    if (!organization?.value || !activity?.value || !client?.value) {
      setNr("");
      setCode("");
      return;
    }

    const reqId = ++previewReqIdRef.current;

    const t = setTimeout(async () => {
      setGenerating(true);
      clearSubmitError();

      try {
        const res = await axiosInstance.post(PREVIEW_URL, {
          organization: organization.value,
          matrix: activity.value,
          client: client.value,
        });

        if (!aliveRef.current || previewReqIdRef.current !== reqId) return;

        setNr(String(res.data?.nr ?? ""));
        setCode(String(res.data?.code ?? ""));
      } catch (e) {
        if (!aliveRef.current) return;

        setNr("");
        setCode("");
        setSubmitError(e);
      } finally {
        if (aliveRef.current) setGenerating(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [
    open,
    organization?.value,
    activity?.value,
    client?.value,
    clearSubmitError,
    setSubmitError,
  ]);

  useEffect(() => {
    if (code && code.trim()) {
      setErrors((prev) => ({ ...prev, code: "" }));
    }
  }, [code]);

  const handleSubmit = async () => {
    setSubmitted(true);
    clearSubmitError();

    if (!validateAll()) return;

    setSaving(true);

    try {
      const opportunity_ids = (selectedOpportunities || []).map((x) => x.value);

      await axiosInstance.post("/code_registries/", {
        organization: organization.value,
        matrix: activity.value,
        client: client.value,
        description: description.trim(),
        remarks: remarks.trim() || null,
        status: "Active",
        opportunity_ids,
      });

      onCreated?.();
      onClose?.();
    } catch (e) {
      setSubmitError(e);
    } finally {
      setSaving(false);
    }
  };

  const showOrgError = submitted && !!errors.organization;
  const showDeptError = submitted && !!errors.department;
  const showActError = submitted && !!errors.activity;
  const showClientError = submitted && !!errors.client;
  const showDescError = submitted && !!errors.description;
  const showOppsError = submitted && !!errors.opportunities;
  const showCodeError = submitted && !!errors.code;

  return (
    <Dialog
      open={open}
      onClose={disabledAll ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      className="project-code-dialog"
    >
      <DialogTitle className="spc-title">
        <span className="spc-header-title">Create Project Code</span>
      </DialogTitle>

      <DialogContent dividers className="cmd-content">
        {/* ORGANIZATION */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Organization <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={organizationOptions}
            value={organization}
            onChange={(val) => {
              setOrganization(val);
              setNr("");
              setCode("");

              if (val) {
                setErrors((prev) => ({ ...prev, organization: "" }));
              }
            }}
            isClearable
            isSearchable
            placeholder="Select organization"
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showOrgError ? "cmd-select-error" : ""}
          />

          {showOrgError && (
            <div className="cmd-field-error">{errors.organization}</div>
          )}
        </div>

        {/* DEPARTMENT */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Department <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={departmentOptions}
            value={department}
            onChange={(val) => {
              setDepartment(val);
              setActivity(null);
              setClient(null);
              setSelectedOpportunities([]);
              setOpportunities([]);
              setNr("");
              setCode("");

              if (val) {
                setErrors((prev) => ({ ...prev, department: "" }));
              }
            }}
            isClearable
            isSearchable
            placeholder="Select department"
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showDeptError ? "cmd-select-error" : ""}
          />

          {showDeptError && (
            <div className="cmd-field-error">{errors.department}</div>
          )}
        </div>

        {/* ACTIVITY */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Activity <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={activityOptions}
            value={activity}
            onChange={(val) => {
              setActivity(val);
              if (val) {
                setErrors((prev) => ({ ...prev, activity: "" }));
              }
            }}
            isClearable
            isSearchable
            placeholder={department ? "Select activity" : "First select a department"}
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll || !department}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showActError ? "cmd-select-error" : ""}
          />

          {showActError && (
            <div className="cmd-field-error">{errors.activity}</div>
          )}
        </div>

        {/* CLIENT */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Client <span style={{ color: "red" }}>*</span>
          </label>

          <Select
            options={clientOptions}
            value={client}
            onChange={(val) => {
              setClient(val);
              setSelectedOpportunities([]);
              setOpportunities([]);

              if (val) {
                setErrors((prev) => ({ ...prev, client: "" }));
              }
            }}
            isClearable
            isSearchable
            placeholder="Select client"
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showClientError ? "cmd-select-error" : ""}
          />

          {showClientError && (
            <div className="cmd-field-error">{errors.client}</div>
          )}
        </div>

        {/* CODE */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Code <span style={{ color: "red" }}>*</span>
          </label>

          <input
            type="text"
            className={`cmd-input ${showCodeError ? "cmd-input-error" : ""}`}
            placeholder="Generated automatically"
            value={code}
            readOnly
            disabled={disabledAll}
          />

          {generating && (
            <div style={{ marginTop: 6 }}>
              <CircularProgress size={18} />
            </div>
          )}

          {showCodeError && (
            <div className="cmd-field-error">{errors.code}</div>
          )}
        </div>

        {/* DESCRIPTION */}
        <div className="cmd-form-group">
          <label className="cmd-label">
            Description <span style={{ color: "red" }}>*</span>
          </label>

          <textarea
            rows={6}
            className={`cmd-input ${showDescError ? "cmd-input-error" : ""}`}
            placeholder="Enter description..."
            value={description}
            onChange={(e) => {
              const val = e.target.value;
              setDescription(val);

              if (val.trim()) {
                setErrors((prev) => ({ ...prev, description: "" }));
              }
            }}
            onBlur={() => {
              if (!submitted) return;

              setErrors((prev) => ({
                ...prev,
                description: description.trim() ? "" : "Required",
              }));
            }}
            disabled={disabledAll}
            style={{ resize: "vertical" }}
          />

          {showDescError && (
            <div className="cmd-field-error">{errors.description}</div>
          )}
        </div>

        {/* OPPORTUNITIES */}
        <div className="cmd-form-group">
          <label className="cmd-label">Opportunities</label>

          <Select
            options={opportunityOptions}
            value={selectedOpportunities}
            onChange={(val) => {
              setSelectedOpportunities(val || []);

              if (val && val.length > 0) {
                setErrors((prev) => ({ ...prev, opportunities: "" }));
              }
            }}
            isMulti
            closeMenuOnSelect={true}
            isClearable
            isSearchable
            placeholder={client ? "Select opportunities" : "Select client first"}
            classNamePrefix="cmd-react-select"
            components={animatedComponents}
            isDisabled={disabledAll || !client}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 20000 }),
              menu: (base) => ({ ...base, zIndex: 20000 }),
            }}
            className={showOppsError ? "cmd-select-error" : ""}
          />

          {showOppsError && (
            <div className="cmd-field-error">{errors.opportunities}</div>
          )}
        </div>

        {/* STATUS */}
        <div className="cmd-form-group">
          <label className="cmd-label">Status</label>
          <input
            type="text"
            className="cmd-input"
            value="Active"
            disabled
            readOnly
          />
        </div>

        {errors.submit && (
          <div className="cmd-submit-error">{errors.submit}</div>
        )}
      </DialogContent>

      <DialogActions className="cmd-actions">
        <Button
          onClick={onClose}
          disabled={disabledAll}
          className="cmd-btn cmd-btn-cancel"
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={disabledAll}
          variant="contained"
          className="cmd-btn cmd-btn-create"
          startIcon={
            saving ? <CircularProgress size={18} className="cmd-spinner" /> : null
          }
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}